



















import { Component, Vue, Watch } from 'vue-property-decorator';
import axios from 'axios';
import { Route } from 'vue-router';
import ConfigModlue from '@/store/modules/config';
import LeafletsModlue from '@/store/modules/leaflets';
import ContentsModlue from '@/store/modules/contents';
import { Config } from '@/types/config';
import { Observe } from '@/types/observe';
import Progress from '@/components/Progress.vue';

@Component({
  components: {
    Progress
  }
})
export default class App extends Vue {
  // 読み込み中フラグ
  private isProgress = true;

  // タイマーiD
  private tickTimerID?: NodeJS.Timeout;

  // 隠し印刷機能の有効／無効
  private isEnableHiddenButton = false;

  // 固定表示モード切り替えの有効／無効
  private isFixedmode = true;

  // 固定表示モード自動切り替えの有効／無効
  private isSyncFixedmode = true;

  // ファイルのバージョン監視
  private observeId: string | null = null;
  private observeFile = '';

  // リロード監視ID
  private reloadId: string | null = null;

  // スリープ状態
  private isSleeping = ConfigModlue.isSleeping;

  @Watch('$route', { immediate: true, deep: true })
  onRouteChange(to: Route, from: Route) {
    if (from !== undefined) {
      // ルートが更新されたときのみ再読み込みを実行
      this.beforeRouteUpdate(to);
    }
  }

  /**
   * 設定ファイルと設定APIを読み込む
   */
  created() {
    this.$store.subscribe(mutation => {
      // 表示モードの切り替え
      if (mutation.type === 'config/setDatas') {
        const routename = ConfigModlue.showFixedmode && this.isSyncFixedmode ? 'fixedmode' : ConfigModlue.routename;
        if (this.$route.name !== routename) {
          this.$router.replace({ name: routename });
        }
      }

      // スリープ ON/OFF
      if (mutation.type === 'config/setSleep' && this.isSleeping !== ConfigModlue.isSleeping) {
        this.isSleeping = ConfigModlue.isSleeping;
        if (!this.isSleeping) {
          // スリープ復帰のときコンテンツをロード
          this.beforeRouteUpdate(this.$route);
        }
      }
    });

    this.isProgress = true;
    Promise.all([
      new Promise(resolve => {
        const timestamp = new Date().getTime();
        axios.get(`${process.env.BASE_URL}config.json?${timestamp}`).then(response => {
          const config: Config = response.data;
          Vue.prototype.$config = config;

          // チラシ設定
          if (config.leaflets !== undefined) {
            config.leaflets.interval !== undefined && LeafletsModlue.setInterval(config.leaflets.interval);
            config.leaflets.wait !== undefined && LeafletsModlue.setWait(config.leaflets.wait);
            config.leaflets.limit !== undefined && LeafletsModlue.setLimit(config.leaflets.limit);
          }

          // 印刷設定
          if (config.print !== undefined) {
            if (config.print.enableHiddenButton !== undefined) {
              this.isEnableHiddenButton = config.print.enableHiddenButton;
            }
          }

          // 固定表示モード設定
          if (config.fixedmode !== undefined) {
            if (config.fixedmode.toggle !== undefined) {
              this.isFixedmode = config.fixedmode.toggle;
            }
            if (config.fixedmode.sync !== undefined) {
              this.isSyncFixedmode = config.fixedmode.sync;
            }
          }

          resolve(config);
        });
      }),
      this.loadObserveAPI()
    ]).then(() => {
      this.isSleeping = ConfigModlue.isSleeping;
      this.beforeRouteUpdate(this.$route);

      // 一定時間ごとに configAPI の更新を監視する
      this.observeConfig();

      // 各種、指定した時間による状態の監視
      this.tickTimerID && clearInterval(this.tickTimerID);
      this.tickTimerID = setInterval(this.tick, 1000);
    });
  }

  /**
   * ルート更新のたびにコンテンツを再読み込みする
   */
  private beforeRouteUpdate(to: Route) {
    // 固定表示モードのURLが設定されていないときは、切り替えボタンを無効にする
    if (ConfigModlue.fixedmodeUri === '') {
      this.isFixedmode = false;
    }

    // スリープ中はコンテンツをロードしない
    if (this.isSleeping) {
      return;
    }

    this.isProgress = true;
    LeafletsModlue.delete();
    const compleat = () => {
      this.isProgress = false;
    };
    if (to.meta && to.meta.isContents) {
      Promise.all([LeafletsModlue.load(), ContentsModlue.load()]).finally(compleat);
    } else {
      Promise.all([LeafletsModlue.load()]).finally(compleat);
    }
  }

  /**
   * 監視APIを読み込む
   */
  private async loadObserveAPI(): Promise<void> {
    return new Promise(resolve => {
      axios
        .get(process.env.VUE_APP_API_DOMAIN + '/api/observe')
        .then(response => {
          const observe: Observe = response.data;

          // APIから端末設定を登録
          ConfigModlue.setDatas(observe.config);
          ConfigModlue.tickSleep();

          // APIから監視ファイルのURLとIDを取得
          this.observeFile = observe.url;
          this.observeId = observe.id;

          // リロード監視IDを設定
          if (observe.action.reload !== undefined) {
            // 初期（NULL）のときはリロードしない
            if (this.reloadId === null) {
              this.reloadId = observe.action.reload;
            } else if (this.reloadId !== observe.action.reload) {
              this.reloadId = observe.action.reload;
              this.$router.go(0);
            }
          }
        })
        .finally(() => {
          resolve();
        });
    });
  }

  /**
   * 各種、指定した時間による状態の監視
   */
  private tick() {
    // 指定した時間になったらスリープにする
    ConfigModlue.tickSleep();

    // 指定した時間になったらリロードする
    ConfigModlue.checkReload().then(() => {
      this.$router.go(0);
    });
  }

  /**
   * 一定時間ごとに configAPI の更新を監視する
   * 10秒に1回読みに行く
   */
  private observeConfig() {
    const timestamp = new Date().getTime();
    axios.get(`${this.observeFile}?${timestamp}`).then(response => {
      const id = response.data as string;
      const uploaded = this.observeId !== id;
      if (uploaded) {
        this.loadObserveAPI().then(() => {
          setTimeout(this.observeConfig, 10000);
        });
      } else {
        setTimeout(this.observeConfig, 10000);
      }
    });
  }

  /**
   * 固定表示モードを切り替え
   */
  private toggleFixedmode() {
    // 監視通信をキャンセル
    const source = this.axios.CancelToken.source();
    source.cancel();

    const routename = this.$route.name !== 'fixedmode' ? 'fixedmode' : ConfigModlue.routename;
    this.$router.replace({ name: routename });

    // サーバー側のDBを更新
    const params = new URLSearchParams();
    params.append('active', routename === 'fixedmode' ? '1' : '0');
    this.axios.patch(process.env.VUE_APP_API_DOMAIN + '/api/fixedmode', params);
  }

  /**
   * 印刷を実行
   */
  private print() {
    if (this.isEnableHiddenButton) {
      LeafletsModlue.print();
    }
  }

  /**
   * 長押しでFullyのラウンチャーを起動
   */
  private launch() {
    document.location.href = 'fully://launcher';
  }
}
